/** @jsxImportSource @emotion/react */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import useEvent from 'react-use-event-hook';
import _ from 'lodash';
import dayjs from 'dayjs';
import { GlobalBtnGroup } from 'components/layouts/ContentLayout';
import { Button, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import { tb } from 'components/layouts/Table';
import cloneDeep from 'lodash/cloneDeep';
import { IconButton } from 'components/buttons/IconSVG';
import { useTranslation } from 'react-i18next';
import { Code } from 'models/common/CommonCode';
import { getCommonCodeNames, getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import { ComboBox } from 'components/selects/ComboBox';
import { useCommonModal } from 'hooks/useCommonModal';
import { useMessageBar } from 'hooks/useMessageBar';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import useSessionStore from 'stores/useSessionStore';
import { ManagementMode, PageMode } from 'models/common/Common';
import { MenuContextType } from 'models/admin/Menu';
import { MenuContext } from 'App';
import { CommonUtil } from 'utils/CommonUtil';
import {
  findProjectById,
  saveProject,
  checkAvailableProjectManager,
} from 'apis/setup/SetupProject';
import { SetupProject } from 'models/setup/SetupProject';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { useLoading } from 'components/process/Loading';
import { FindInputBtnSet } from 'components/layouts/TableInButton';
import PartnerModal from 'components/modals/common/PartnerModal';
import UserMultiModal from 'components/modals/common/UserMultiModal';
import { Employee } from 'models/admin/Employee';
import { getEmployeeByCondition } from 'apis/admin/Employee';
import CustomEditorInputWithSearch from 'components/inputs/CustomEditorInputWithSearch';
import CrossEditor from 'components/crosseditor/CrossEditor';
import { DateRangeType } from 'react-tailwindcss-datepicker';
import CustomDatepicker from 'components/inputs/CustomDatepicker';
import { hasRole } from 'utils/SessionUtil';

interface PageLocationState {
  mode?: PageMode;
  elmPrjId?: string;
}

/**
 * @desc 설비셋업관리 > 프로젝트 기준정보 > 프로젝트 등록/조회 > 프로젝트 상세
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.08.24   jshwang12       조직유형 코드 추가 (tb_eelmb_prj_m.elm_prj_tp_cd) 프로젝트 등록완료 후 수정불가
 */
const SetupProjectRegistPage = (props) => {
  const labelSx = {
    // width: 190,
  };

  const valueSx = {
    // width: 220,
  };

  const { t } = useTranslation();
  const { isLoading } = useLoading();
  const { openCommonModal } = useCommonModal();
  const { openMessageBar } = useMessageBar();
  const menuContext = useContext<MenuContextType>(MenuContext);

  const [searchParams] = useSearchParams();
  const [locationState, setLocationState] = useState<PageLocationState>(useLocation().state);

  const location = useLocation();
  const state = { ...location.state };
  const [errors, setErrors] = useState<any>({});

  const navigate = useNavigate();

  const [hasMgrAuth, setHasMgrAuth] = useState<boolean>(false);
  const [hasPrjMgrAuth, setHasPrjMgrAuth] = useState<boolean>(false);
  const [hasWriteUserAuth, setHasWriteUserAuth] = useState<boolean>(false);

  // 수정 여부
  const [elmPrjId, setElmPrjId] = useState<string>(locationState.elmPrjId ?? '');
  const [editMode, setEditMode] = useState<string>(locationState?.mode ?? 'WRITE');
  const [isReadOnly, setReadOnly] = useState<boolean>(false);
  const { userId, empNm } = useSessionStore();

  // 프로젝트 ID 채번 룰 : PRJ + ‘-’ +   SEQ No(5자리)
  // const [elmPrjIdNew, setElmPrjIdNew] = useState<string>('');

  // 공통코드 (combo box list)  bildPlntCd : 공장, askPurpCd : 목적, utLineQty : 라인수량, utmWkProgStatCd : 진행상태
  const [bildPlntCd, setBildPlntCd] = useState<Code[]>([]);
  const [prjDivsCd, setPrjDivsCd] = useState<Code[]>([]);
  const [prjSinTpl, setPrjSinTpl] = useState<Code[]>([]);
  const [cellFertTpCd, setCellFertTpCd] = useState<Code[]>([]);
  const [prjOrgTpCd, setPrjOrgTpCd] = useState<Code[]>([]); // [26.08.24] 조직유형 코드 추가

  // 공통코드 api call
  const getCommonCodes = async () => {
    Promise.all([
      getCommonCodeNames('FA_SU_PLNT_CD'),
      getCommonCodeNames('PRJ_DIVS_CD'),
      getCommonCodeNames('PRJ_SIN_TPL'),
      getCommonCodeNames('CELL_FERT_TP_CD'),
      getCommonCodeNames('PRJ_ORG_TP_CD'),
    ]).then(([bildPlntCd, prjDivsCd, prjSinTpl, cellFertTpCd, prjOrgTpCd]) => {
      setBildPlntCd(bildPlntCd); // 공장
      setPrjDivsCd(prjDivsCd); //구분
      setPrjSinTpl(prjSinTpl);
      setCellFertTpCd(cellFertTpCd);
      setPrjOrgTpCd(prjOrgTpCd);
    });
  };

  const editorRef = useRef<CrossEditor>(null);

  const [isEditorReady, setEditorReady] = useState(false);
  const basicTemplete = useMemo(() => {
    const tpl = prjSinTpl
      .filter((o) => o.cmnCd === 'PRJ_DTL')
      .map((o) => o.optValCtn1)
      .toString();
    if (!_.isEmpty(tpl)) {
      return tpl;
    }
    return '';
  }, [prjSinTpl]);

  const [isOpenPartnerModal, setOpenPartnerModal] = useState<boolean>(false);

  const [isOpenSetupHqMgrMultiModal, setOpenSetupHqMgrMultiModal] = useState<boolean>(false);
  const [isOpenSetupCopMgrMultiModal, setOpenSetupCopMgrMultiModal] = useState<boolean>(false);

  const [originMasterData, setOriginMasterData] = useState<SetupProject>({});
  const [masterData, setMasterData] = useState<SetupProject>({});
  const [managers, setManagers] = useState<Employee[]>([]); // FA, 법인 담당자 정보
  const optionsReqManager = useMemo(() => {
    // 대표담당자 옵션 목록
    return (managers || []).reduce(
      (acc, cur) => [
        ...acc,
        {
          cmnCd: cur.userId,
          cmnCdNm: cur.empNm,
        },
      ],
      [] as Code[]
    );
  }, [managers]);

  const isCreateProjectAuth = useMemo(() => {
    if (hasRole('FA_MANAGER') || hasRole('FA_USER') || hasRole('FA_USER_COP')) {
      return true;
    }
    return false;
  }, []);

  // 수정권한체크 -> FA 담당자(본사/법인), FA_MANAGER
  useEffect(() => {
    checkAvailableProjectManager(elmPrjId, 'ALL').then((res) => setHasPrjMgrAuth(res));
  }, [elmPrjId, editMode]);

  useEffect(() => {
    setHasMgrAuth(hasRole('FA_MANAGER'));
  }, [elmPrjId, editMode]);

  useEffect(() => {
    setHasWriteUserAuth(userId === originMasterData?.dataInsUserId);
  }, [elmPrjId, editMode, originMasterData?.dataInsUserId]);

  const setSafeBody = (ref, content) => {
    const ed = ref.current?.editorRef;
    if (!ed) {
      setTimeout(() => setSafeBody(ref, content), 50);
      return;
    }
    ed?.SetBodyValue(CommonUtil.safeHtmlContents(content ?? '', false));
  };

  const isEditable = (column = '') => {
    // 신규모드인 경우
    if (editMode === PageMode.WRITE) {
      return true;
    }
    // 수정모드인 경우
    else if (editMode === PageMode.EDIT) {
      // 임시저장인 경우 모두 편집 가능
      if (masterData.elmPrjStatCd === 'SAVE') return true;
      // 수정가능항목
      const cols = [
        'bildPlntCd', // 플랜트
        'prjDivsCd', // 구분
        'ivesPeriod', // 프로젝트 기간
        'elmPrjNm', // 프로젝트명
        'suHqMgrNm', // FA 담당자
        'suCopMgrNm', // 법인 담당자
        'reqMgrId', // 대표담당자
        'excpNm', // 참여업체
        'eqpPrdnLnQty', // 라인수
        'prdnCapa', // CAPA
        'wbsCd', // WBS
        'ivesAmt', // 투자금액
        'wkDtlCtn', // 내용
        'cellFertTpCd',
        'eltrEqpQty',
        'asyEqpQty',
        'mduEqpQty',
      ];
      return cols.includes(column);
    }
    return false;
  };

  // 임시저장상태일때 사용 가능 체크
  const isAvailableDraftMode = useMemo(() => {
    // 프로젝트가 임시저장 상태일때 작성자만 접근가능
    if (originMasterData.elmPrjStatCd === 'SAVE' && originMasterData?.dataInsUserId !== userId) {
      return false;
    } else {
      return true;
    }
  }, [editMode, originMasterData]);

  useEffect(() => {
    //공통코드 호출
    getCommonCodes();
  }, []);

  useEffect(() => {
    if (state?.elmPrjId) {
      setLocationState(state);
    }
  }, [location]);

  useEffect(() => {
    if (locationState?.elmPrjId && editMode !== PageMode.WRITE) {
      setEditMode(PageMode.READ);
      setElmPrjId(locationState.elmPrjId || searchParams.get('elmPrjId') || '');
    }
  }, [locationState?.elmPrjId]);

  useEffect(() => {
    if (elmPrjId) {
      // 프로젝트 id 변경시 read 모드로 전환
      if (editMode === PageMode.DELETE) return;
      (async () => {
        await loadProject(elmPrjId);
      })();
    }
  }, [elmPrjId, editMode]);

  useEffect(() => {
    let isCloseTab = false;
    // 프로젝트 정보 조회후 -> 권한체크
    if (!isAvailableDraftMode) {
      openMessageBar({
        type: 'error',
        content: t(
          'com.label.해당 프로젝트의 작성자가 아닙니다.',
          '해당 프로젝트의 작성자가 아닙니다.'
        ),
      });

      isCloseTab = true;
    }

    if (isCloseTab) {
      menuContext.closeCurrentTab(menuContext);
      return;
    }
  }, [originMasterData]);

  useEffect(() => {
    // FA, 법인 담당자 정보 조회
    const userIds = [masterData?.suHqMgrId, masterData?.suCopMgrId].filter((o) => !!o).join(',');
    if (userIds) {
      getEmployeeByCondition({ userIds: userIds }).then((result) => {
        setManagers(result || []);
        // 설정된 대표담당자가 담당자 목록에서 사라진 경우 대표담당자 값 초기화
        if (!(result || []).filter((o) => o.userId === masterData?.reqMgrId).length) {
          handleItemChange('reqMgrId', '');
        }
      });
    } else {
      setManagers([]);
      handleItemChange('reqMgrId', '');
    }
  }, [masterData?.suHqMgrId, masterData?.suCopMgrId]);

  const loadProject = async (elmPrjId) => {
    findProjectById(elmPrjId).then((result: SetupProject) => {
      if (!result) {
        openMessageBar({
          type: 'error',
          content: t('setup.msg.프로젝트 정보가 없습니다.', '프로젝트 정보가 없습니다.'),
        });
        menuContext.closeCurrentTab(menuContext);
        return;
      }

      result.excpIds = result.excpId?.split(',').map((v) => v.trim());

      setOriginMasterData(cloneDeep(result));
      setMasterData(cloneDeep(result));
    });
  };

  const callbackPrjHqMgrData = (callbackData) => {
    if (callbackData) {
      setMasterData({
        ...masterData,
        suHqMgrId: callbackData.userId,
        suHqMgrNm: callbackData.empNm,
      });
      setErrors((prev) => ({
        ...prev,
        suHqMgrNm: false,
      }));
    }
  };

  const callbackPrjCopMgrData = (callbackData) => {
    if (callbackData) {
      setMasterData({
        ...masterData,
        suCopMgrId: callbackData.userId,
        suCopMgrNm: callbackData.empNm,
      });
      setErrors((prev) => ({
        ...prev,
        suCopMgrNm: false,
      }));
    }
  };

  const validate = (mode: 'DRAFT' | 'SAVE' | 'DELETE') => {
    const err = {};

    // 플랜트
    if (!masterData?.bildPlntCd) {
      Object.assign(err, { bildPlntCd: true });
    }
    // 구분
    if (!masterData?.prjDivsCd) {
      Object.assign(err, { prjDivsCd: true });
    }
    // 프로젝트명
    if (!masterData?.elmPrjNm) {
      Object.assign(err, { elmPrjNm: true });
    }
    // 유형
    if (!masterData?.cellFertTpCd) {
      Object.assign(err, { cellFertTpCd: true });
    }
    if ('SAVE' === mode) {
      // 조직유형 코드
      if (!masterData?.elmPrjTpCd) {
        Object.assign(err, { elmPrjTpCd: true });
      }
      // 프로젝트 기간
      if (!masterData?.ivesStDt || !masterData?.ivesEndDt) {
        Object.assign(err, { ivesPeriod: true });
      }
      // 담당자
      if (!masterData?.suHqMgrNm) {
        Object.assign(err, { suHqMgrNm: true });
      }
      // 법인 담당자
      if (!masterData?.suCopMgrNm) {
        Object.assign(err, { suCopMgrNm: true });
      }
      // 대표담당자
      if (!masterData?.reqMgrId) {
        Object.assign(err, { reqMgrId: true });
      }
      // 참여업체
      if (!masterData?.excpNm) {
        Object.assign(err, { excpNm: true });
      }
    }

    setErrors(err);
    if (!Object.keys(err).filter((k) => err[k]).length) {
      return true;
    }
    return false;
  };

  /**
   * 저장
   */
  const btnSave = useCallback(
    async (mode: 'DRAFT' | 'SAVE' | 'DELETE') => {
      if (mode !== 'DELETE' && !validate(mode)) {
        return;
      }

      let saveRowsData = masterData;
      saveRowsData = {
        ...saveRowsData,
        saveType: mode,
        editMode: mode === 'DELETE' ? PageMode.DELETE : editMode,
      };

      openCommonModal({
        modalType: 'confirm',
        content:
          mode !== 'DELETE'
            ? t('com.label.저장하시겠습니까?', '저장하시겠습니까?')
            : t(
                'setup.label.해당 프로젝트를 삭제 하시겠습니까?',
                '해당 프로젝트를 삭제 하시겠습니까?'
              ),
        yesCallback: async () => {
          const response = await saveProject({
            ...saveRowsData,
          });

          if (response == null || response?.successOrNot === 'N') {
            openMessageBar({
              type: 'error',
              content:
                typeof response?.data === 'string'
                  ? response?.data
                  : mode !== 'DELETE'
                  ? t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.')
                  : t('com.label.삭제 중 오류가 발생했습니다.', '삭제 중 오류가 발생했습니다.'),
            });
            return;
          }
          openMessageBar({
            type: 'confirm',
            content:
              mode !== 'DELETE'
                ? t('com.label.저장되었습니다.', '저장되었습니다.')
                : t('com.label.삭제되었습니다.', '삭제되었습니다.'),
          });
          if (response.data) {
            if (mode !== 'DELETE') {
              // 재조회
              setEditMode(PageMode.READ);
              setElmPrjId(response.data ?? '');
            } else {
              // 현재 탭 닫기
              menuContext.closeCurrentTab(menuContext);
              const searchPageCondition = {
                upprMnuUrl: location.pathname,
                mnuId: `${location.pathname}/${ManagementMode.MODIFY}`,
                mnuNm: t('setup.label.프로젝트 등록/조회', '프로젝트 등록/조회'),
              };
              navigate(`/setup/project`, {
                state: { ...searchPageCondition },
              });
            }
          }
        },
      });
    },
    [masterData, editMode]
  );

  const handleConditionChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const name = e.target.name;
    const value = e.target.value;
    setMasterData({
      ...masterData,
      [name]: value || '',
    });

    setErrors((prev) => ({
      ...prev,
      [name]: false,
    }));
  };

  const handleItemChange = (name: string, value: string | string[]) => {
    setMasterData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: false,
    }));
  };

  useEffect(() => {
    if (isEditorReady) {
      editorRef.current?.editorRef.SetReadonly(!isEditable('wkDtlCtn'));
      setSafeBody(editorRef, originMasterData.wkDtlCtn);
    }
  }, [isEditorReady, editorRef.current, editMode, originMasterData.wkDtlCtn]);

  useEffect(() => {
    if (isEditorReady && editMode === PageMode.WRITE && basicTemplete) {
      setSafeBody(editorRef, basicTemplete ?? '');
    }
  }, [isEditorReady, editorRef.current, basicTemplete]);

  const handleEditorOnLoad = (ref, value) => {
    if (!ref.current) return;
    setEditorReady(true);
  };

  const handleEditorChange = useEvent((e) => {
    const target = e?.editorName;
    const targetText = e.editorTarget.GetBodyValue();
    setErrors({ ...errors, [target]: false });

    handleItemChange(target, targetText);
    editorRef.current?.editorRef.SaveBodyValue?.();
  });

  const btnDelete = () => {
    // 삭제
    //setEditMode(PageMode.DELETE);
    btnSave('DELETE');
  };

  const btnCancel = () => {
    // 상세모드로 전환
    if (editMode === PageMode.WRITE) {
      menuContext.closeCurrentTab(menuContext);
    } else {
      setEditMode(PageMode.READ);
    }
  };

  const btnModify = () => {
    // 수정모드로 전환
    setEditMode(PageMode.EDIT);
  };

  return (
    <>
      <TableContainer css={tb.table} className="colFix9">
        <Table>
          <TableBody>
            <TableRow>
              <TableCell sx={labelSx}>
                <span className={isEditable('bildPlntCd') ? 'dot' : ''}>
                  {t('setup.label.플랜트', '플랜트')}
                </span>
              </TableCell>
              <TableCell sx={valueSx}>
                {isEditable('bildPlntCd') ? (
                  <ComboBox
                    options={bildPlntCd}
                    placeholder={String(t('com.label.선택', '선택'))}
                    defaultValue={masterData?.bildPlntCd}
                    disabled={isReadOnly}
                    onChange={(value) => {
                      handleItemChange('bildPlntCd', value);
                    }}
                    isError={errors?.bildPlntCd}
                    msgError={String(
                      t('setup.msg.플랜트를 선택해 주세요.', '플랜트를 선택해 주세요.')
                    )}
                  />
                ) : (
                  originMasterData.bildPlntCdNm
                )}
              </TableCell>
              <TableCell sx={labelSx}>
                <span className={isEditable('prjDivsCd') ? 'dot' : ''}>
                  {t('setup.label.구분', '구분')}
                </span>
              </TableCell>
              <TableCell sx={valueSx}>
                {isEditable('prjDivsCd') ? (
                  <ComboBox
                    options={prjDivsCd}
                    placeholder={String(t('com.label.선택', '선택'))}
                    defaultValue={masterData?.prjDivsCd}
                    disabled={isReadOnly}
                    onChange={(value) => {
                      handleItemChange('prjDivsCd', value);
                    }}
                    isError={errors?.prjDivsCd}
                    msgError={String(t('setup.msg.구분을 선택해 주세요.', '구분을 선택해 주세요.'))}
                  />
                ) : (
                  originMasterData.prjDivsCdNm
                )}
              </TableCell>
              <TableCell sx={labelSx}>
                <span className={isEditable('cellFertTpCd') ? 'dot' : ''}>
                  {t('setup.label.유형', '유형')}
                </span>
              </TableCell>
              <TableCell sx={valueSx}>
                {isEditable('cellFertTpCd') ? (
                  <ComboBox
                    options={cellFertTpCd}
                    placeholder={String(t('com.label.선택', '선택'))}
                    defaultValue={masterData?.cellFertTpCd}
                    disabled={isReadOnly}
                    onChange={(value) => {
                      handleItemChange('cellFertTpCd', value);
                    }}
                    isError={errors?.cellFertTpCd}
                    msgError={String(t('setup.msg.유형을 선택해 주세요.', '유형을 선택해 주세요.'))}
                  />
                ) : (
                  originMasterData.cellFertTpCdNm
                )}
              </TableCell>
              <TableCell sx={labelSx}>
                <span className={isEditable('ivesPeriod') ? 'dot' : ''}>
                  {t('setup.label.프로젝트 기간', '프로젝트 기간')}
                </span>
              </TableCell>
              <TableCell sx={valueSx}>
                {isEditable('ivesPeriod') ? (
                  <CustomDatepicker
                    minDate={null}
                    value={{
                      startDate: masterData?.ivesStDt || '',
                      endDate: masterData?.ivesEndDt || '',
                    }}
                    onChange={(newValue) => {
                      const value = newValue as DateRangeType;
                      setMasterData((prev) => ({
                        ...prev,
                        ivesStDt: (value?.startDate as string) || '',
                        ivesEndDt: (value?.endDate as string) || '',
                      }));
                      setErrors((prev) => ({
                        ...prev,
                        ivesPeriod: false,
                      }));
                    }}
                    isError={errors?.ivesPeriod}
                    msgError={String(
                      t(
                        'setup.label.프로젝트 기간을 입력해 주세요.',
                        '프로젝트 기간을 입력해 주세요.'
                      )
                    )}
                    placeholder={String(
                      t('setup.label.기본 품의 ~ 양산 시작', '기본 품의 ~ 양산 시작')
                    )}
                  />
                ) : (
                  CommonUtil.formatPeriod(
                    originMasterData.ivesStDt ?? '',
                    originMasterData.ivesEndDt ?? ''
                  )
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={labelSx}>
                <span className={isEditable('elmPrjNm') ? 'dot' : ''}>
                  {t('setup.label.프로젝트명', '프로젝트명')}
                </span>
              </TableCell>
              <TableCell sx={valueSx} colSpan={5}>
                {isEditable('elmPrjNm') ? (
                  <CustomInputWithSearch
                    id="elmPrjNm"
                    name="elmPrjNm"
                    type="text"
                    value={masterData?.elmPrjNm}
                    placeholder={String(
                      t('setup.msg.프로젝트 명을 입력해 주세요.', '프로젝트 명을 입력해 주세요.')
                    )}
                    onChange={(e) => handleConditionChange(e)}
                    isError={errors?.elmPrjNm}
                    msgError={String(
                      t('setup.msg.프로젝트 명을 입력해 주세요.', '프로젝트 명을 입력해 주세요.')
                    )}
                  ></CustomInputWithSearch>
                ) : (
                  originMasterData.elmPrjNm
                  //CommonUtil.safeHtmlContents(originMasterData.elmPrjNm, false)
                )}
              </TableCell>
              <TableCell sx={labelSx}>
                <span className={isEditable('elmPrjTpCd') ? 'dot' : ''}>
                  {t('setup.label.조직유형', '조직유형')}
                </span>
              </TableCell>
              <TableCell sx={valueSx}>
                {isEditable('elmPrjTpCd') ? (
                  <ComboBox
                    options={prjOrgTpCd}
                    placeholder={String(t('com.label.선택', '선택'))}
                    defaultValue={masterData?.elmPrjTpCd}
                    disabled={isReadOnly}
                    onChange={(value) => {
                      handleItemChange('elmPrjTpCd', value);
                    }}
                    isError={errors?.elmPrjTpCd}
                    msgError={String(
                      t('setup.msg.조직유형을 선택해 주세요.', '조직유형을 선택해 주세요.')
                    )}
                  />
                ) : (
                  originMasterData.elmPrjTpCdNm
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={labelSx}>
                <span className={isEditable('suHqMgrNm') ? 'dot' : ''}>
                  {t('setup.label.FA 담당자(본사)', 'FA 담당자(본사)')}
                </span>
              </TableCell>
              <TableCell sx={valueSx} colSpan={5}>
                {isEditable('suHqMgrNm') ? (
                  <FindInputBtnSet>
                    <CustomInputWithSearch
                      name="suHqMgrNm"
                      className="find"
                      placeholder={String(
                        t(
                          'setup.label.FA 담당자(본사)를 입력해 주세요.',
                          'FA 담당자(본사)를 입력해 주세요.'
                        )
                      )}
                      value={masterData?.suHqMgrNm}
                      readOnly={!isEditable('suHqMgrNm')}
                      onSearchClick={() => {
                        if (isEditable('suHqMgrNm')) {
                          setOpenSetupHqMgrMultiModal(true);
                        }
                      }}
                      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === 'Enter') {
                          if (isEditable('suHqMgrNm')) {
                            setOpenSetupHqMgrMultiModal(true);
                          }
                        }
                      }}
                      isError={errors?.suHqMgrNm}
                      msgError={String(
                        t(
                          'mp.label.FA 담당자(본사)를 선택해 주세요.',
                          'FA 담당자(본사)를 선택해 주세요.'
                        )
                      )}
                    />
                  </FindInputBtnSet>
                ) : (
                  originMasterData.suHqMgrNm
                )}
              </TableCell>
              <TableCell sx={labelSx}>
                <span>{t('setup.label.상태', '상태')}</span>
              </TableCell>
              <TableCell sx={valueSx}>
                {originMasterData?.elmPrjStatCdNm || t('setup.label.신규등록', '신규등록')}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={labelSx}>
                <span className={isEditable('suCopMgrNm') ? 'dot' : ''}>
                  {t('setup.label.FA 담당자(법인)', 'FA 담당자(법인)')}
                </span>
              </TableCell>
              <TableCell sx={valueSx} colSpan={5}>
                {isEditable('suCopMgrNm') ? (
                  <FindInputBtnSet>
                    <CustomInputWithSearch
                      name="suCopMgrNm"
                      className="find"
                      placeholder={String(
                        t(
                          'setup.label.FA 담당자(법인)를 입력해 주세요.',
                          'FA 담당자(법인)를 입력해 주세요.'
                        )
                      )}
                      value={masterData?.suCopMgrNm}
                      readOnly={!isEditable('suCopMgrNm')}
                      onSearchClick={() => {
                        if (isEditable('suCopMgrNm')) {
                          setOpenSetupCopMgrMultiModal(true);
                        }
                      }}
                      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === 'Enter') {
                          if (isEditable('suCopMgrNm')) {
                            setOpenSetupCopMgrMultiModal(true);
                          }
                        }
                      }}
                      isError={errors?.suCopMgrNm}
                      msgError={String(
                        t(
                          'mp.label.FA 담당자(법인)를 선택해 주세요.',
                          'FA 담당자(법인)를 선택해 주세요.'
                        )
                      )}
                    />
                  </FindInputBtnSet>
                ) : (
                  originMasterData.suCopMgrNm
                )}
              </TableCell>
              <TableCell sx={labelSx}>
                <span className={isEditable('reqMgrId') ? 'dot' : ''}>
                  {t('setup.label.대표담당자', '대표담당자')}
                </span>
              </TableCell>
              <TableCell sx={valueSx}>
                {isEditable('reqMgrId') ? (
                  <ComboBox
                    options={optionsReqManager}
                    placeholder={String(t('com.label.선택', '선택'))}
                    defaultValue={masterData?.reqMgrId}
                    disabled={isReadOnly}
                    onChange={(value) => {
                      handleItemChange('reqMgrId', value);
                    }}
                    isError={errors?.reqMgrId}
                    msgError={String(
                      t('setup.msg.대표담당자를 선택해 주세요.', '대표담당자를 선택해 주세요.')
                    )}
                  />
                ) : (
                  originMasterData.reqMgrNm
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={labelSx}>
                <span className={isEditable('excpNm') ? 'dot' : ''}>
                  {t('setup.label.참여업체', '참여업체')}
                </span>
              </TableCell>
              <TableCell sx={valueSx} colSpan={7}>
                {isEditable('excpNm') ? (
                  <FindInputBtnSet>
                    <CustomInputWithSearch
                      name="excpNm"
                      className="find"
                      placeholder={String(
                        t('setup.label.참여업체를 입력해 주세요.', '참여업체를 입력해 주세요.')
                      )}
                      value={masterData?.excpNm}
                      readOnly={!isEditable('excpNm')}
                      onSearchClick={() => {
                        if (isEditable('excpNm')) {
                          setOpenPartnerModal(true);
                        }
                      }}
                      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === 'Enter') {
                          if (isEditable('excpNm')) {
                            setOpenPartnerModal(true);
                          }
                        }
                      }}
                      isError={errors?.excpNm}
                      msgError={String(
                        t('mp.label.참여업체를 입력해 주세요.', '참여업체를 입력해 주세요.')
                      )}
                    />
                  </FindInputBtnSet>
                ) : (
                  originMasterData.excpNm
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              {/*<TableCell>
                <span>{t('setup.label.라인수(전극 기준)', '라인수(전극 기준)')}</span>
              </TableCell>
              <TableCell>
                {isEditable('eqpPrdnLnQty') ? (
                  <CustomInputWithSearch
                    id="eqpPrdnLnQty"
                    name="eqpPrdnLnQty"
                    value={masterData?.eqpPrdnLnQty}
                    placeholder={String(
                      t(
                        'setup.msg.라인수(전극 기준)을 입력해 주세요.',
                        '라인수(전극 기준)을 입력해 주세요.'
                      )
                    )}
                    onInput={(e) => {
                      const target = e.target as HTMLInputElement;
                      target.value = target.value.replace(/[^0-9]/g, '');
                    }}
                    onChange={(e) => handleConditionChange(e)}
                  />
                ) : (
                  <span>
                    {CommonUtil.defaultZeroNumberCellTemplate(originMasterData.eqpPrdnLnQty)}
                  </span>
                )}
              </TableCell>*/}
              {/*<TableCell colSpan={1} />*/}
              <TableCell sx={labelSx}>
                <span>{t('setup.label.라인수(전극 기준)', '라인수(전극 기준)')}</span>
              </TableCell>
              <TableCell sx={valueSx} colSpan={3}>
                {isEditable('eltrEqpQty') ? (
                  <CustomInputWithSearch
                    id="eltrEqpQty"
                    name="eltrEqpQty"
                    value={masterData?.eltrEqpQty}
                    placeholder={String(
                      t(
                        'setup.msg.라인수(전극 기준)을 입력해 주세요.',
                        '라인수(전극 기준)을 입력해 주세요.'
                      )
                    )}
                    onInput={(e) => {
                      const target = e.target as HTMLInputElement;
                      target.value = target.value
                        .replace(/[^0-9.]/g, '')
                        .replace(/(\..*)\./g, '$1');
                    }}
                    onChange={(e) => handleConditionChange(e)}
                  ></CustomInputWithSearch>
                ) : (
                  <span>
                    {CommonUtil.defaultZeroNumberCellTemplate(originMasterData.eltrEqpQty)}
                  </span>
                )}
              </TableCell>
              <TableCell sx={labelSx}>
                <span>{t('setup.label.라인수(CELL 기준)', '라인수(CELL 기준)')}</span>
              </TableCell>
              <TableCell sx={valueSx}>
                {isEditable('asyEqpQty') ? (
                  <CustomInputWithSearch
                    id="asyEqpQty"
                    name="asyEqpQty"
                    type="text"
                    value={masterData?.asyEqpQty}
                    placeholder={String(
                      t(
                        'setup.msg.라인수(CELL 기준)을 입력해 주세요.',
                        '라인수(CELL 기준)을 입력해 주세요.'
                      )
                    )}
                    onInput={(e) => {
                      const target = e.target as HTMLInputElement;
                      target.value = target.value
                        .replace(/[^0-9.]/g, '')
                        .replace(/(\..*)\./g, '$1');
                    }}
                    onChange={(e) => handleConditionChange(e)}
                  ></CustomInputWithSearch>
                ) : (
                  CommonUtil.defaultZeroNumberCellTemplate(originMasterData.asyEqpQty)
                )}
              </TableCell>
              <TableCell sx={labelSx}>
                <span>{t('setup.label.라인수(모듈 기준)', '라인수(모듈 기준)')}</span>
              </TableCell>
              <TableCell sx={valueSx}>
                {isEditable('mduEqpQty') ? (
                  <CustomInputWithSearch
                    id="mduEqpQty"
                    name="mduEqpQty"
                    type="text"
                    value={masterData?.mduEqpQty}
                    placeholder={String(
                      t(
                        'setup.msg.라인수(모듈 기준)을 입력해 주세요.',
                        '라인수(모듈 기준)을 입력해 주세요.'
                      )
                    )}
                    onInput={(e) => {
                      const target = e.target as HTMLInputElement;
                      target.value = target.value
                        .replace(/[^0-9.]/g, '')
                        .replace(/(\..*)\./g, '$1');
                    }}
                    onChange={(e) => handleConditionChange(e)}
                  ></CustomInputWithSearch>
                ) : (
                  CommonUtil.defaultZeroNumberCellTemplate(originMasterData.mduEqpQty)
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={labelSx}>
                <span>{t('setup.label.생산 Capa(GWh)', '생산 Capa(GWh)')}</span>
              </TableCell>
              <TableCell sx={valueSx} colSpan={3}>
                {isEditable('prdnCapa') ? (
                  <CustomInputWithSearch
                    id="prdnCapa"
                    name="prdnCapa"
                    value={masterData?.prdnCapa}
                    placeholder={String(
                      t(
                        'setup.msg.생산 Capa(GWh)을 입력해 주세요.',
                        '생산 Capa(GWh)을 입력해 주세요.'
                      )
                    )}
                    onInput={(e) => {
                      const target = e.target as HTMLInputElement;
                      target.value = target.value
                        .replace(/[^0-9.]/g, '')
                        .replace(/(\..*)\./g, '$1');
                    }}
                    onChange={(e) => handleConditionChange(e)}
                  ></CustomInputWithSearch>
                ) : (
                  <span>{CommonUtil.defaultZeroNumberCellTemplate(originMasterData.prdnCapa)}</span>
                )}
              </TableCell>
              <TableCell sx={labelSx}>
                <span>{t('setup.label.WBS CD', 'WBS CD')}</span>
              </TableCell>
              <TableCell sx={valueSx}>
                {isEditable('wbsCd') ? (
                  <CustomInputWithSearch
                    id="wbsCd"
                    name="wbsCd"
                    type="text"
                    value={masterData?.wbsCd}
                    placeholder={String(
                      t('setup.msg.WBS CD를 입력해 주세요.', 'WBS CD를 입력해 주세요.')
                    )}
                    onChange={(e) => handleConditionChange(e)}
                  ></CustomInputWithSearch>
                ) : (
                  originMasterData.wbsCd
                )}
              </TableCell>
              <TableCell sx={labelSx}>
                <span>{t('setup.label.투자비(억원)', '투자비(억원)')}</span>
              </TableCell>
              <TableCell sx={valueSx}>
                {isEditable('ivesAmt') ? (
                  <CustomInputWithSearch
                    id="ivesAmt"
                    name="ivesAmt"
                    value={masterData?.ivesAmt}
                    placeholder={String(
                      t('setup.msg.투자비를 입력해 주세요.', '투자비를 입력해 주세요.')
                    )}
                    onChange={(e) => handleConditionChange(e)}
                    onInput={(e) => {
                      const target = e.target as HTMLInputElement;
                      target.value = target.value
                        .replace(/[^0-9.]/g, '')
                        .replace(/(\..*)\./g, '$1');
                    }}
                    isError={errors?.ivesAmt}
                    msgError={String(
                      t('setup.msg.투자비를 입력해 주세요.', '투자비를 입력해 주세요.')
                    )}
                  ></CustomInputWithSearch>
                ) : (
                  <span>
                    {CommonUtil.defaultZeroNumberCellTemplate(originMasterData.ivesAmt, 2)}
                  </span>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={labelSx}>
                <span>{t('setup.label.상세 내용', '상세 내용')}</span>
              </TableCell>
              <TableCell sx={valueSx} colSpan={7}>
                {
                  <CustomEditorInputWithSearch
                    editorRef={editorRef}
                    name={'wkDtlCtn'}
                    value={masterData?.wkDtlCtn}
                    isError={errors?.wkDtlCtn}
                    msgError={String(
                      t('setup.label.상세 내용을 입력해 주세요.', '상세 내용을 입력해 주세요.')
                    )}
                    style={{
                      height: '400',
                    }}
                    isReadOnly={true}
                    onKeyActive={handleEditorChange}
                    onLoaded={() => handleEditorOnLoad(editorRef, masterData?.wkDtlCtn)}
                    readOnly
                  />
                }
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={labelSx}>{String(t('mp.label.등록자', '등록자'))}</TableCell>
              <TableCell sx={valueSx}>{masterData?.dataInsUserNm || empNm}</TableCell>
              <TableCell sx={labelSx}>{String(t('mp.label.등록일', '등록일'))}</TableCell>
              <TableCell sx={valueSx}>
                {dayjs(masterData?.dataInsDtm).format('YYYY.MM.DD')}
              </TableCell>
              <TableCell sx={labelSx}>{String(t('mp.label.수정자', '수정자'))}</TableCell>
              <TableCell sx={valueSx}>{masterData?.dataUpdUserNm}</TableCell>
              <TableCell sx={labelSx}>{String(t('mp.label.최종수정일', '최종수정일'))}</TableCell>
              <TableCell sx={valueSx}>
                {masterData?.dataUpdDtm && dayjs(masterData?.dataUpdDtm).format('YYYY.MM.DD')}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <GlobalBtnGroup>
        {editMode !== PageMode.READ && (
          <>
            <Button css={IconButton.button} className="cancel" disableRipple onClick={btnCancel}>
              {t('com.button.취소', '취소')}
            </Button>
          </>
        )}
        {editMode === PageMode.EDIT && masterData?.elmPrjStatCd === 'SAVE' && (
          <>
            <Button css={IconButton.button} className="delete" disableRipple onClick={btnDelete}>
              {t('com.button.삭제', '삭제')}
            </Button>
          </>
        )}
        {editMode !== PageMode.WRITE &&
          editMode === PageMode.READ &&
          (hasMgrAuth || hasPrjMgrAuth || hasWriteUserAuth) && (
            <>
              <Button css={IconButton.button} className="edit" disableRipple onClick={btnModify}>
                {t('com.button.수정', '수정')}
              </Button>
            </>
          )}
        {(editMode === PageMode.EDIT || editMode === PageMode.WRITE) &&
          masterData?.elmPrjStatCd !== 'REG_CPLT' && (
            <>
              <Button
                css={IconButton.button}
                className="icoNone"
                disabled={isLoading}
                onClick={() => btnSave('DRAFT')}
              >
                {t('com.button.임시저장', '임시저장')}
              </Button>
            </>
          )}
        {(editMode === PageMode.EDIT || editMode === PageMode.WRITE) && (
          <>
            <Button
              css={IconButton.button}
              className="save"
              disabled={isLoading}
              onClick={() => btnSave('SAVE')}
            >
              {t('com.label.저장', '저장')}
            </Button>
          </>
        )}
      </GlobalBtnGroup>

      {isOpenPartnerModal && (
        <PartnerModal
          open={isOpenPartnerModal}
          close={() => setOpenPartnerModal(false)}
          mode={'fa'}
          isOnlyUsed={true}
          singleSelect={false}
          selectedIds={masterData?.excpId ? masterData.excpId.split(',').map((o) => o.trim()) : []}
          onCallback={(result) => {
            const excpId = CommonUtil.nomalizeToArray(result)
              .map((o) => o.excpId)
              .join(', ');
            const excpNm = CommonUtil.nomalizeToArray(result)
              .map((o) => o.excpNm)
              .join(', ');
            setMasterData({
              ...masterData,
              excpId: excpId,
              excpNm: excpNm,
            });
            setErrors((prev) => ({
              ...prev,
              excpNm: false,
            }));
          }}
        />
      )}
      {isOpenSetupHqMgrMultiModal && (
        <UserMultiModal
          open={isOpenSetupHqMgrMultiModal}
          close={() => setOpenSetupHqMgrMultiModal(false)}
          title={t('com.label.FA 담당자(본사) 조회', 'FA 담당자(본사) 조회')}
          condition={{
            roles: ['FA_USER'],
          }}
          defaultUserId={masterData.suHqMgrId || ''}
          singleSelect={false}
          onCallback={(callbackData: any) => {
            if (callbackData.userId === '') {
              openMessageBar({
                type: 'warning',
                content: t('com.msg.선택된 사용자가 없습니다.', '선택된 사용자가 없습니다.'),
              });
              return false;
            }
            callbackPrjHqMgrData(callbackData);
          }}
        />
      )}
      {isOpenSetupCopMgrMultiModal && (
        <UserMultiModal
          open={isOpenSetupCopMgrMultiModal}
          close={() => setOpenSetupCopMgrMultiModal(false)}
          title={t('com.label.FA 담당자(법인) 조회', 'FA 담당자(법인) 조회')}
          condition={{
            roles: ['FA_USER_COP'],
          }}
          defaultUserId={masterData.suCopMgrId || ''}
          singleSelect={false}
          onCallback={(callbackData: any) => {
            if (callbackData.userId === '') {
              openMessageBar({
                type: 'warning',
                content: t('com.msg.선택된 사용자가 없습니다.', '선택된 사용자가 없습니다.'),
              });
              return false;
            }
            callbackPrjCopMgrData(callbackData);
          }}
        />
      )}
    </>
  );
};

export default SetupProjectRegistPage;
